import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DASHBOARD_SIGNUP_URL, TRACKING_URL } from '@/config/links';
import { ROUTES } from '@/config/routes';

const FAQSection = () => {
  const faqCtaClass =
    'group mt-4 h-11 rounded-full border-0 bg-gradient-to-r from-blue-800 via-blue-800 to-blue-900 px-5 text-white shadow-lg shadow-blue-700/30 transition-all duration-300 hover:-translate-y-0.5 hover:from-blue-900 hover:to-blue-950 hover:shadow-xl hover:shadow-blue-700/40';

  const faqs = [
    {
      question: 'How do I get started with Boxd Logistics?',
      answer: 'Simply sign up on our platform, complete the verification process, and you can start shipping immediately. There are no setup fees or long-term commitments required.',
      ctaLabel: 'Get Started',
      ctaHref: DASHBOARD_SIGNUP_URL,
      ctaExternal: true
    },
    {
      question: 'Which carriers do you support?',
      answer: 'We integrate with major carriers including Blue Dart, DHL, DTDC, Delhivery, Ekart, Shadowfax, and XpressBees. You can choose the best option for each shipment.'
    },
    {
      question: 'How does pricing work?',
      answer: 'Our pricing is transparent and competitive. You only pay for the shipments you make, with rates varying based on weight, distance, and selected carrier. Use our rate calculator to get instant estimates.',
      ctaLabel: 'View Pricing',
      ctaHref: ROUTES.PRICING
    },
    {
      question: 'Can I track my shipments in real-time?',
      answer: 'Yes! All shipments include real-time tracking. Both you and your customers can monitor the package location and estimated delivery time throughout the journey.',
      ctaLabel: 'Track Order',
      ctaHref: TRACKING_URL
    },
    {
      question: 'Does Boxd Logistics support international shipping?',
      answer: 'Yes, Boxd Logistics supports international shipping through trusted carrier partners. Our team can help you choose the right service levels, transit timelines, and documentation flow for your destination countries.'
    },
    {
      question: 'Do you offer customer support?',
      answer: 'We provide 24/7 customer support via phone, email, and chat. Our dedicated team is always ready to assist with any questions or issues.'
    },
    {
      question: 'How do I integrate Boxd with my eCommerce store?',
      answer: 'We offer easy integration options for major eCommerce platforms. Our technical team will guide you through the setup process, which typically takes less than an hour.'
    }
  ];

  return (
    <section className="relative py-24 bg-white overflow-hidden" id="faq">
      {/* Decorative Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
        
        {/* Floating orbs */}
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-gradient-to-br from-blue-100/40 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
            Frequently Asked <span className="text-blue-800">Questions</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Find answers to common questions about our services
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-3xl p-8 md:p-12 border-2 border-gray-200 shadow-xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border border-gray-200 px-6 hover:border-blue-300 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-800 py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  <p>{faq.answer}</p>
                  {faq.ctaLabel && faq.ctaHref && (
                    faq.ctaExternal ? (
                      <a href={faq.ctaHref} target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                        <Button className={faqCtaClass}>
                          <span>{faq.ctaLabel}</span>
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Button>
                      </a>
                    ) : (
                      <Link to={faq.ctaHref} className="inline-block mt-4">
                        <Button className={faqCtaClass}>
                          <span>{faq.ctaLabel}</span>
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Button>
                      </Link>
                    )
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;